import React, { useState, useEffect, useMemo } from 'react';
import { 
  X, Car, Plus, Users, MapPin, CalendarDays, Trash2, CheckCircle, AlertTriangle, Sparkles, AlertCircle, Clock, Building2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarWidget } from '../ui/calendar';
import AddressAutocomplete from '../AddressAutocomplete';
import { toast } from 'sonner';
import { cleaningApi } from '../../lib/cleaningApi';
import { getVehicleMakes, getVehicleModels, getVehicleYears, getVehicleColors } from '../../lib/api';
import { TrackSchedulePreview } from '../admin/TrackSchedulePreview';
import { formatCurrency, getBookingPriceBreakdown } from '../../lib/cleaningPricing';

const ADMIN_BOOKING_MIN_DATE = new Date(2026, 2, 15);
const ADMIN_BOOKING_MAX_DATE = new Date(2026, 2, 31);

function isAdminDisabledDay(date) {
  return date.getDay() === 6; // Saturday
}

function formatAdminDateDisplay(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function dateToStr(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getServiceCategoryFromType(serviceType) {
  return serviceType === 'commercial' ? 'commercial' : 'residential';
}

const COMMERCIAL_ADMIN_TIME_SLOTS = [
  "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM"
];

const RESIDENTIAL_ADMIN_TIME_SLOTS = [
  "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM"
];

const DETAILING_TIME_SLOTS = ["9:00 AM", "11:00 AM", "1:00 PM", "3:00 PM"];
const UNKNOWN_VEHICLE_VALUE = 'Unknown';
const DEFAULT_SERVICE_CATALOG = {
  residential: {
    name: 'Platinum Package',
    sizes: {
      sedan: { label: 'Sedan', price: 85 },
      '2_row_suv': { label: '2 Row SUV', price: 105 },
      '3_row_suv': { label: '3 Row SUV', price: 115 },
      minivan: { label: 'Minivan', price: 125 },
    },
  },
  full_detailing: {
    name: 'Full Detailing',
    sizes: {
      sedan: { label: 'Sedan', price: 200 },
      '2_row_suv': { label: '2 Row SUV', price: 260 },
      '3_row_suv': { label: '3 Row SUV', price: 300 },
      minivan: { label: 'Minivan', price: 350 },
    },
  },
  commercial: {
    name: 'Commercial Service',
    sizes: {
      commercial: { label: 'Commercial Fleet', price: 0 },
    },
  },
};

function getResolvedServiceCatalog(serviceCatalog) {
  return serviceCatalog && Object.keys(serviceCatalog).length ? serviceCatalog : DEFAULT_SERVICE_CATALOG;
}

function getTimeItemClass(isUnavailable) {
  return isUnavailable ? 'bg-slate-100 text-slate-400 line-through opacity-100' : '';
}

function parseTimeToMinutes(timeString) {
  if (!timeString) return 0;
  const match = timeString.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) return 0;

  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const period = match[3].toUpperCase();

  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;

  return hours * 60 + minutes;
}

function getScheduleTrack(item = {}) {
  if (item.service_type === 'commercial' || item.service_category === 'commercial') return 'commercial';
  if (item.service_type === 'full_detailing') return 'full_detailing';
  return 'residential';
}

function getBookingDurationMinutes(item = {}) {
  const totalVehicles = Number(item.total_vehicles || item.vehicle_count || 1) || 1;
  return item.service_type === 'full_detailing' ? totalVehicles * 120 : totalVehicles * 60;
}

function getBookingCapacityUsage(item, serviceType) {
  if (serviceType === 'full_detailing') return 1;
  if (item.crew_assignment === 'both') return 2;
  if (item.crew_assignment === 'crew_a' || item.crew_assignment === 'crew_b') return 1;
  return serviceType === 'commercial' ? 2 : 1;
}

function getBlockCapacityUsage(item, serviceType) {
  if (serviceType === 'full_detailing') return item.crew_target === 'detailing' ? 1 : 0;
  if (item.crew_target === 'both' || item.crew_target === 'all') return 2;
  if (item.crew_target === 'crew_a' || item.crew_target === 'crew_b') return 1;
  return 0;
}

function getAdminBookingDurationMinutes(serviceType, vehicles, commercialEndTime, startTime) {
  if (serviceType === 'commercial') {
    if (!commercialEndTime || !startTime) return 0;
    const duration = parseTimeToMinutes(commercialEndTime) - parseTimeToMinutes(startTime);
    return Math.max(duration, 0);
  }

  const perVehicleMinutes = serviceType === 'full_detailing' ? 120 : 60;
  return Math.max(vehicles.length, 1) * perVehicleMinutes;
}

function formatDurationLabel(minutes) {
  if (!minutes) return '0h';
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (!remainder) return `${hours}h`;
  return `${hours}h ${remainder}m`;
}

function getAdminTimeSlots(dateStr, serviceType = 'residential') {
  const isDetailing = serviceType === 'full_detailing';
  const isCommercial = serviceType === 'commercial';
  const baseSlots = isDetailing
    ? DETAILING_TIME_SLOTS
    : isCommercial
      ? COMMERCIAL_ADMIN_TIME_SLOTS
      : RESIDENTIAL_ADMIN_TIME_SLOTS;
  if (!dateStr) return baseSlots;
  const d = new Date(dateStr + 'T00:00:00');
  const isFriday = d.getDay() === 5;
  if (isFriday) {
    const jobDuration = isDetailing ? 120 : 60; // 2hr for detailing, 1hr for residential
    return baseSlots.filter(slot => {
      const parts = slot.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (!parts) return false;
      let h = parseInt(parts[1]);
      const m = parseInt(parts[2]);
      const period = parts[3].toUpperCase();
      if (period === 'PM' && h !== 12) h += 12;
      if (period === 'AM' && h === 12) h = 0;
      const endMins = (h * 60 + m) + jobDuration;
      return endMins <= 16.5 * 60; // 4:30 PM = 990 mins
    });
  }
  return baseSlots;
}

export default function AdminBookingModal({ isOpen, onClose, onSuccess, bookingToEdit = null }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [serviceType, setServiceType] = useState('residential');
  const [vehicleSize, setVehicleSize] = useState('');
  const [customer, setCustomer] = useState({ name: '', phone: '', email: '' });
  const [address, setAddress] = useState({ street: '', city: '', state: 'NJ', zip_code: '', lat: null, lng: null });
  const [date, setDate] = useState('');
  const [time, setTime] = useState('9:00 AM');
  const [payment, setPayment] = useState('pending');
  const [endTime, setEndTime] = useState('5:00 PM');
  const [crewAssignment, setCrewAssignment] = useState('both');
  
  const [slotAvailability, setSlotAvailability] = useState({});
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState({});
  const [daySchedule, setDaySchedule] = useState({ bookings: [], time_blocks: [] });
  const [loadingSchedule, setLoadingSchedule] = useState(false);

  const [processPayment, setProcessPayment] = useState(false);
  const [status, setStatus] = useState('confirmed');
  const [notes, setNotes] = useState('');
  const [commercialPrice, setCommercialPrice] = useState('');
  const [commercialCount, setCommercialCount] = useState(1);
  const [override, setOverride] = useState(false);
  const [allowedDates, setAllowedDates] = useState(null);
  const [zone, setZone] = useState('');
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [colors, setColors] = useState([]);
  const [currentVehicle, setCurrentVehicle] = useState({ year: '', make: '', model: '', color: '' });
  const [serviceCatalog, setServiceCatalog] = useState(DEFAULT_SERVICE_CATALOG);
  
  // New fields
  const [referralSource, setReferralSource] = useState('');
  const [corporateId, setCorporateId] = useState('');

  // Initialize form when opening/editing
  useEffect(() => {
    if (isOpen) {
      if (bookingToEdit) {
        // Edit Mode: Populate Form
        setServiceType(bookingToEdit.service_type || 'residential');
        setCustomer({
             name: bookingToEdit.customer?.full_name || '',
             phone: bookingToEdit.customer?.phone || '',
             email: bookingToEdit.customer?.email || ''
        });

        setAddress({
          street: bookingToEdit.service_address?.street || '',
          city: bookingToEdit.service_address?.city || '',
          state: bookingToEdit.service_address?.state || 'NJ',
          zip_code: bookingToEdit.service_address?.zip_code || '',
          lat: bookingToEdit.service_address?.lat,
          lng: bookingToEdit.service_address?.lng
        });
        setDate(bookingToEdit.preferred_date || '');
        setTime(bookingToEdit.preferred_time || '9:00 AM');
        setEndTime(bookingToEdit.end_time || '5:00 PM');
        setPayment(bookingToEdit.payment_status || 'pending');
        setStatus(bookingToEdit.status || 'confirmed');
        setNotes(bookingToEdit.notes || '');
        setCrewAssignment(bookingToEdit.crew_assignment || 'both');
        setOverride(bookingToEdit.override_zone_restriction || false);
        setReferralSource(bookingToEdit.referral_source || '');
        setCorporateId(bookingToEdit.corporate_id || '');
        
        // Reconstruct vehicles array
        if (bookingToEdit.service_type === 'commercial' || bookingToEdit.service_category === 'commercial') {
             setCommercialPrice(bookingToEdit.price_per_vehicle || '');
             setCommercialCount(bookingToEdit.vehicle_count || 1);
             const newVehicle = {
                id: Date.now(),
                service_type: 'commercial', service_name: 'Commercial Job', vehicle_size: 'commercial',
                vehicle_size_label: 'Commercial Fleet', price: bookingToEdit.price, vehicle_count: bookingToEdit.vehicle_count,
                price_per_vehicle: bookingToEdit.price_per_vehicle, vehicle: { year: new Date().getFullYear().toString(), make: 'Commercial', model: `${bookingToEdit.vehicle_count} Vehicles`, color: 'N/A' }
             };
             setVehicles([newVehicle]);
        } else {
             const vs = [];
             if (bookingToEdit.vehicle) {
                 vs.push({
                     id: Date.now(),
                     service_type: bookingToEdit.service_type,
                     service_name: bookingToEdit.service_name,
                     vehicle_size: bookingToEdit.vehicle_size,
                     vehicle_size_label: bookingToEdit.vehicle_size_label,
                     price: bookingToEdit.price_per_vehicle || bookingToEdit.price, 
                     vehicle: bookingToEdit.vehicle,
                     vehicle_count: 1,
                     price_per_vehicle: bookingToEdit.price_per_vehicle
                 });
             }
             if (bookingToEdit.additional_vehicles) {
                 bookingToEdit.additional_vehicles.forEach((av, i) => {
                     vs.push({
                         id: Date.now() + i + 1,
                         service_type: av.service_type,
                         service_name: av.service_name,
                         vehicle_size: av.vehicle_size,
                         vehicle_size_label: av.vehicle_size_label,
                         price: av.price,
                         vehicle: av.vehicle
                     });
                 });
             }
             setVehicles(vs);
        }

      } else {
        // Create Mode: Reset Form
        setServiceType('residential');
        setVehicles([]);
        setCustomer({ name: '', phone: '', email: '' });
        setAddress({ street: '', city: '', state: 'NJ', zip_code: '', lat: null, lng: null });
        setDate('');
        setTime('9:00 AM');
        setEndTime('5:00 PM');
        setPayment('pending');
        setStatus('confirmed');
        setNotes('');
        setCommercialPrice('');
        setCommercialCount(1);
        setOverride(false);
        setProcessPayment(false);
        setReferralSource('');
        setCorporateId('');
      }
    }
  }, [isOpen, bookingToEdit]);

  useEffect(() => {
    if (serviceType === 'commercial') {
      const slots = getAdminTimeSlots(date, serviceType);
      const startIdx = slots.indexOf(time);
      if (startIdx >= 0) {
        const defaultEnd = slots[Math.min(slots.length - 1, startIdx + 2)];
        if (!endTime || slots.indexOf(endTime) <= startIdx) {
            setEndTime(defaultEnd);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time, serviceType, date]);

  const timeSelectKey = `${serviceType}-${date || 'no-date'}-${loadingSlots ? 'loading' : JSON.stringify(slotAvailability)}`;
  const vehicleCountForAvailability = serviceType === 'commercial'
    ? Math.max(Number(commercialCount || 1), 1)
    : Math.max(vehicles.length, 1);
  const estimatedDurationMinutes = getAdminBookingDurationMinutes(serviceType, vehicles, endTime, time);
  
  const derivedUnavailableTimes = useMemo(() => {
    const serviceSlots = getAdminTimeSlots(date, serviceType);
    const serviceCapacity = serviceType === 'full_detailing' ? 1 : 2;
    const occupancyBySlot = {};

    serviceSlots.forEach((slot) => {
      occupancyBySlot[slot] = 0;
    });

    (daySchedule.bookings || []).forEach((booking) => {
      if (bookingToEdit && booking.id === bookingToEdit.id) return;

      if (getScheduleTrack(booking) !== serviceType || booking.status === 'cancelled') return;

      const startMinute = parseTimeToMinutes(booking.preferred_time);
      const endMinute = booking.end_time ? parseTimeToMinutes(booking.end_time) : startMinute + getBookingDurationMinutes(booking);
      const capacityUsage = getBookingCapacityUsage(booking, serviceType);

      serviceSlots.forEach((slot) => {
        const slotMinute = parseTimeToMinutes(slot);
        if (slotMinute >= startMinute && slotMinute < endMinute) {
          occupancyBySlot[slot] += capacityUsage;
        }
      });
    });

    (daySchedule.time_blocks || []).forEach((block) => {
      const capacityUsage = getBlockCapacityUsage(block, serviceType);
      if (!capacityUsage) return;

      const startMinute = parseTimeToMinutes(block.start_time);
      const endMinute = parseTimeToMinutes(block.end_time) || (startMinute + 60);

      serviceSlots.forEach((slot) => {
        const slotMinute = parseTimeToMinutes(slot);
        if (slotMinute >= startMinute && slotMinute < endMinute) {
          occupancyBySlot[slot] += capacityUsage;
        }
      });
    });

    return serviceSlots.reduce((acc, slot) => {
      acc[slot] = occupancyBySlot[slot] >= serviceCapacity;
      return acc;
    }, {});
  }, [date, daySchedule.bookings, daySchedule.time_blocks, serviceType, bookingToEdit]);

  useEffect(() => {
    if (isOpen) {
      const loadVehicleData = async () => {
        try {
          const [makesRes, yearsRes, colorsRes, servicesRes] = await Promise.all([
            getVehicleMakes(),
            getVehicleYears(),
            getVehicleColors(),
            cleaningApi.getServices()
          ]);
          setMakes(makesRes.data.makes || []);
          setYears(yearsRes.data.years || []);
          setColors(colorsRes.data.colors || []);
          setServiceCatalog(servicesRes.data?.services || DEFAULT_SERVICE_CATALOG);
        } catch (err) {
          console.error('Failed to load vehicle data:', err);
          setServiceCatalog(DEFAULT_SERVICE_CATALOG);
        }
      };
      loadVehicleData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const start = '2026-03-01';
    const end = '2026-03-31';

    cleaningApi.getAdminCalendarAvailability(start, end, serviceType)
      .then(res => setCalendarStatus(res.data || {}))
      .catch(() => setCalendarStatus({}));
  }, [isOpen, serviceType]);

  useEffect(() => {
    if (!date) {
      setSlotAvailability({});
      setDaySchedule({ bookings: [], time_blocks: [] });
      return;
    }

    const serviceCategory = getServiceCategoryFromType(serviceType);
    setLoadingSlots(true);
    setLoadingSchedule(true);

    cleaningApi.getAvailability(date, vehicleCountForAvailability, true, serviceType, '', '', '', '', '', serviceCategory)
      .then(res => {
        const slots = res.data.slots || [];
        const availabilityMap = {};
        slots.forEach((slot) => {
          availabilityMap[slot.time] = slot.available;
        });
        setSlotAvailability(availabilityMap);
      })
      .catch(() => setSlotAvailability({}))
      .finally(() => setLoadingSlots(false));

    cleaningApi.getDaySchedule(date)
      .then(res => {
        setDaySchedule({
          bookings: res.data.bookings || [],
          time_blocks: res.data.time_blocks || []
        });
      })
      .catch(() => setDaySchedule({ bookings: [], time_blocks: [] }))
      .finally(() => setLoadingSchedule(false));
  }, [date, serviceType, vehicleCountForAvailability]);

  useEffect(() => {
    if (currentVehicle.make && currentVehicle.make !== UNKNOWN_VEHICLE_VALUE) {
      getVehicleModels(currentVehicle.make).then(res => {
        setModels(res.data.models || []);
      }).catch(() => setModels([]));
    } else {
      setModels([]);
    }
  }, [currentVehicle.make]);

  useEffect(() => {
    if (serviceType === 'residential' && address.street && address.city && address.zip_code) {
      cleaningApi.checkZone(address.street, address.city, address.zip_code, address.lat, address.lng)
        .then(res => {
          setAllowedDates(res.data.allowed_dates || []);
          setZone(res.data.zone);
          if (!override && date && res.data.allowed_dates && !res.data.allowed_dates.includes(date)) {
             if (!bookingToEdit) { 
                 setDate('');
                 toast.info(`Date cleared: Zone ${res.data.zone} requires specific days.`);
             }
          }
        })
        .catch(() => { setAllowedDates([]); setZone(''); });
    } else {
      setAllowedDates(null);
      setZone('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address.street, address.city, address.zip_code, serviceType, override, bookingToEdit]);

  const isDateDisabled = (d) => {
    // Disable past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (d < today) return true;

    if (isAdminDisabledDay(d)) return true;
    if (serviceType === 'residential' && allowedDates && !override) {
       const dStr = dateToStr(d);
       return !allowedDates.includes(dStr);
    }
    return false;
  };

  const getDisabledMatcher = () => {
    if (override) return isAdminDisabledDay;
    return isDateDisabled;
  };

  const renderDay = (day) => {
      const dStr = dateToStr(day);
      const status = calendarStatus[dStr];
      return (
          <div className="relative w-full h-full flex items-center justify-center">
              <span>{day.getDate()}</span>
              {status && (
                  <div className="absolute bottom-1 flex gap-0.5">
                      {status.has_crew_a && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" title="Crew A Busy" />}
                      {status.has_crew_b && <div className="w-1.5 h-1.5 rounded-full bg-orange-500" title="Crew B Busy" />}
                      {status.has_detailing && <div className="w-1.5 h-1.5 rounded-full bg-cyan-500" title="Detailing Busy" />}
                  </div>
              )}
          </div>
      );
  };

  const handleAddVehicle = () => {
    if (!serviceType) { toast.error('Please select service type'); return; }
    if (serviceType === 'commercial') {
      if (!commercialPrice || !commercialCount) { toast.error('Please enter price and quantity'); return; }
      const count = parseInt(commercialCount);
      const pricePer = parseFloat(commercialPrice);
      const newVehicle = {
        id: Date.now(),
        service_type: 'commercial', service_name: 'Commercial Job', vehicle_size: 'commercial',
        vehicle_size_label: 'Commercial Fleet', price: pricePer * count, vehicle_count: count,
        price_per_vehicle: pricePer, vehicle: { year: new Date().getFullYear().toString(), make: 'Commercial', model: `${count} Vehicles`, color: 'N/A' }
      };
      setVehicles(prev => [...prev, newVehicle]);
      setCommercialPrice(''); setCommercialCount(1);
      toast.success('Commercial job added');
      return;
    }
    
    if (!vehicleSize) { toast.error('Please select vehicle size'); return; }
    if (vehicles.length > 0 && vehicles[0].service_type !== serviceType) {
      toast.error('All vehicles in one booking must use the same service type');
      return;
    }
    if (!currentVehicle.year || !currentVehicle.make || !currentVehicle.model || !currentVehicle.color) { toast.error('Please fill in vehicle info'); return; }
    const resolvedCatalog = getResolvedServiceCatalog(serviceCatalog);
    const serviceInfo = resolvedCatalog[serviceType];
    const sizeInfo = serviceInfo?.sizes?.[vehicleSize];
    if (!serviceInfo || !sizeInfo) {
      toast.error('Unable to load backend pricing for this vehicle');
      return;
    }

    const newVehicle = {
      id: Date.now(), service_type: serviceType, service_name: serviceInfo.name,
      vehicle_size: vehicleSize, vehicle_size_label: sizeInfo.label,
      price: sizeInfo.price || 0, vehicle: { ...currentVehicle },
      price_per_vehicle: sizeInfo.price
    };
    setVehicles(prev => [...prev, newVehicle]);
    setCurrentVehicle({ year: '', make: '', model: '', color: '' });
    setVehicleSize('');
    toast.success('Vehicle added');
  };

  const handleRemoveVehicle = (id) => setVehicles(prev => prev.filter(v => v.id !== id));

  const handleSubmit = async () => {
      if (vehicles.length === 0) { toast.error('Please add at least one vehicle'); return; }
      if (!customer.name || !customer.phone || !customer.email) { toast.error('Please fill in customer info'); return; }
      if (!address.street || !address.city || !address.zip_code) { toast.error('Please fill in address'); return; }
      if (!date) { toast.error('Please select a date'); return; }
      
      setIsSubmitting(true);
      try {
        const firstVehicle = vehicles[0];
        const bookingData = {
            service_type: firstVehicle.service_type,
            service_category: serviceType === 'commercial' ? 'commercial' : 'residential',
            vehicle_size: firstVehicle.vehicle_size,
            customer_name: customer.name, customer_phone: customer.phone, customer_email: customer.email,
            customer: { full_name: customer.name, phone: customer.phone, email: customer.email },
            vehicle_year: firstVehicle.vehicle.year, vehicle_make: firstVehicle.vehicle.make,
            vehicle_model: firstVehicle.vehicle.model, vehicle_color: firstVehicle.vehicle.color,
            vehicle: firstVehicle.vehicle,
            street: address.street, city: address.city, state: address.state, zip_code: address.zip_code,
            service_address: { street: address.street, city: address.city, state: address.state, zip_code: address.zip_code, lat: address.lat, lng: address.lng },
            preferred_date: date, preferred_time: time, payment_status: payment, status: status, notes: notes,
            override_zone_restriction: override, lat: address.lat, lng: address.lng,
            vehicle_count: firstVehicle.vehicle_count, price_per_vehicle: firstVehicle.price_per_vehicle,
            price: firstVehicle.price,
            total_vehicles: vehicles.length,
            additional_vehicles: vehicles.slice(1).map(v => ({
              service_type: v.service_type,
              vehicle_size: v.vehicle_size,
              vehicle: v.vehicle,
              price: v.price
            })),
            end_time: serviceType === 'commercial' ? endTime : undefined,
            crew_assignment: serviceType === 'commercial' ? crewAssignment : undefined,
            referral_source: referralSource,
            corporate_id: corporateId
        };

        if (bookingToEdit) {
            await cleaningApi.updateBooking(bookingToEdit.id, bookingData);
            toast.success('Booking updated successfully');
        } else {
            const bookingRes = await cleaningApi.createAdminBooking(bookingData);
            const bookingId = bookingRes.data.id;

            if (processPayment) {
                toast.success('Booking created. Opening Stripe checkout...');
                const originUrl = window.location.origin;
                const checkoutRes = await cleaningApi.createCheckout(bookingId, originUrl);
                if (checkoutRes.data.url) {
                    window.open(checkoutRes.data.url, '_blank');
                } else {
                    toast.error('Failed to generate checkout link');
                }
            } else {
                toast.success(`Booking created with ${vehicles.length} vehicle${vehicles.length === 1 ? '' : 's'}`);
            }
        }
        
        onSuccess(); onClose();
      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save booking'); } 
      finally { setIsSubmitting(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="add-booking-dialog">
      <div className="bg-white rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-slate-900 font-['Outfit']">{bookingToEdit ? `Edit Booking #${bookingToEdit.id.substring(0,6)}` : 'Add New Booking'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-6">
          {/* Service Selection */}
          <div className="bg-slate-50 rounded-xl p-4 mb-4">
             <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Sparkles className="w-5 h-5 text-emerald-600" /> Service Selection</h3>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Service Type</label>
                    <Select key={`service-type-${serviceType}`} value={serviceType} onValueChange={(val) => {
                      const typeChanged = val !== serviceType;
                      setServiceType(val);
                      const slots = getAdminTimeSlots(date, val);
                      if (!slots.includes(time)) setTime(slots[0] || '9:00 AM');
                      if (typeChanged && vehicles.length > 0) {
                        setVehicles([]);
                        toast.info('Vehicles were cleared because the booking type changed.');
                      }
                      setVehicleSize('');
                      setCurrentVehicle({ year: '', make: '', model: '', color: '' });
                    }}>
                      <SelectTrigger data-testid="add-booking-service-type"><SelectValue placeholder="Select service" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residential">Platinum Package (Pesach)</SelectItem>
                        <SelectItem value="full_detailing">Full Detailing</SelectItem>
                        <SelectItem value="commercial">Commercial Job</SelectItem>
                      </SelectContent>
                    </Select>
                 </div>
                 {serviceType === 'residential' && (
                     <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Corporate ID (Optional)</label>
                        <div className="relative">
                            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input 
                                placeholder="Enter Corp ID" 
                                value={corporateId} 
                                onChange={e => setCorporateId(e.target.value)} 
                                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-emerald-500" 
                            />
                        </div>
                     </div>
                 )}
             </div>
          </div>

          {/* Add Vehicle / Commercial Details */}
          <div className="bg-slate-50 rounded-xl p-4">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Car className="w-5 h-5 text-emerald-600" /> {serviceType === 'commercial' ? 'Commercial Job Details' : 'Add Vehicle'}</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              {serviceType === 'commercial' ? (
                <div className="flex gap-4 col-span-2">
                  <div className="flex-1">
                     <label className="block text-sm font-medium text-slate-700 mb-1">Price Per Vehicle</label>
                     <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                        <input type="number" value={commercialPrice} onChange={e => setCommercialPrice(e.target.value)} className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-emerald-500" placeholder="0.00" />
                     </div>
                  </div>
                  <div className="flex-1">
                     <label className="block text-sm font-medium text-slate-700 mb-1">Quantity</label>
                     <input type="number" value={commercialCount} onChange={e => setCommercialCount(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-emerald-500" min="1" />
                  </div>
                </div>
              ) : (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Vehicle Size</label>
                <Select value={vehicleSize} onValueChange={setVehicleSize}>
                  <SelectTrigger data-testid="add-booking-vehicle-size"><SelectValue placeholder="Select size" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sedan">Sedan</SelectItem>
                    <SelectItem value="2_row_suv">2 Row SUV</SelectItem>
                    <SelectItem value="3_row_suv">3 Row SUV</SelectItem>
                    <SelectItem value="minivan">Minivan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              )}
            </div>
            {/* ... Residential Vehicle Dropdowns (Year/Make/Model/Color) ... */}
            {serviceType !== 'commercial' && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Year</label><Select value={currentVehicle.year} onValueChange={v => setCurrentVehicle(p => ({ ...p, year: v }))}><SelectTrigger><SelectValue placeholder="Year" /></SelectTrigger><SelectContent className="max-h-60">{years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent></Select></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Make</label><Select value={currentVehicle.make} onValueChange={v => setCurrentVehicle(p => ({ ...p, make: v, model: '' }))}><SelectTrigger><SelectValue placeholder="Make" /></SelectTrigger><SelectContent className="max-h-60">{makes.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Model</label><Select value={currentVehicle.model} onValueChange={v => setCurrentVehicle(p => ({ ...p, model: v }))} disabled={!currentVehicle.make}><SelectTrigger><SelectValue placeholder={currentVehicle.make ? "Model" : "Select make first"} /></SelectTrigger><SelectContent className="max-h-60">{models.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Color</label><Select value={currentVehicle.color} onValueChange={v => setCurrentVehicle(p => ({ ...p, color: v }))}><SelectTrigger><SelectValue placeholder="Color" /></SelectTrigger><SelectContent className="max-h-60">{colors.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            </div>
            )}
            <Button onClick={handleAddVehicle} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="add-vehicle-to-booking-btn"><Plus className="w-4 h-4 mr-2" /> {serviceType === 'commercial' ? 'Set Commercial Job Details' : 'Add Vehicle to Booking'}</Button>
          </div>

          {/* Added Vehicles List */}
          {vehicles.length > 0 && (
            <div>
              <h3 className="font-semibold text-slate-800 mb-3">Vehicles ({vehicles.length})</h3>
              <div className="space-y-2">
                {vehicles.map((v, idx) => (
                  <div key={v.id} className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center font-bold">{idx + 1}</span>
                      <div>
                        <p className="font-medium text-slate-800">{v.vehicle.year} {v.vehicle.make} {v.vehicle.model}</p>
                        <p className="text-sm text-slate-600" data-testid={`admin-booking-vehicle-base-price-${v.id}`}>{v.service_name} - ${formatCurrency(getBookingPriceBreakdown(v).basePrice)}</p>
                        <p className="text-[11px] text-slate-500" data-testid={`admin-booking-vehicle-total-with-tax-${v.id}`}>Total w/ tax: ${formatCurrency(getBookingPriceBreakdown(v).totalWithTax)}</p>
                      </div>
                    </div>
                    <button onClick={() => handleRemoveVehicle(v.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="admin-booking-duration-summary">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-semibold text-slate-800">Estimated booking duration</span>
                  <span className="font-bold text-emerald-700">{formatDurationLabel(estimatedDurationMinutes)}</span>
                </div>
                <p className="mt-1 text-[11px] text-slate-500">
                  {serviceType === 'full_detailing'
                    ? `${vehicles.length} vehicle${vehicles.length === 1 ? '' : 's'} × 2 hours each`
                    : serviceType === 'commercial'
                      ? 'Commercial duration uses the manual start and end time you set.'
                      : `${vehicles.length} vehicle${vehicles.length === 1 ? '' : 's'} × 1 hour each`}
                </p>
              </div>
            </div>
          )}

          {/* Customer & Address */}
          <div>
            <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Users className="w-5 h-5 text-emerald-600" /> Customer Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <input placeholder="Full Name *" value={customer.name} onChange={e => setCustomer(p => ({ ...p, name: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500" data-testid="add-booking-customer-name" />
                <input placeholder="Phone *" value={customer.phone} onChange={e => setCustomer(p => ({ ...p, phone: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500" data-testid="add-booking-customer-phone" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input type="email" placeholder="Email *" value={customer.email} onChange={e => setCustomer(p => ({ ...p, email: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500" data-testid="add-booking-customer-email" />
                <input placeholder="Referral Source (Optional)" value={referralSource} onChange={e => setReferralSource(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><MapPin className="w-5 h-5 text-emerald-600" /> Service Address</h3>
            <div className="space-y-3">
              <AddressAutocomplete onAddressSelect={(addr) => setAddress({ street: addr.street || addr.formatted_address || '', city: addr.city || '', state: addr.state || 'NJ', zip_code: addr.zip_code || '', lat: addr.lat || null, lng: addr.lng || null })} placeholder="Start typing address..." />
              <div className="grid grid-cols-3 gap-3">
                <input placeholder="City *" value={address.city} onChange={e => setAddress(p => ({ ...p, city: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500" data-testid="add-booking-city" />
                <input placeholder="State" value={address.state} disabled className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none bg-slate-50" />
                <input placeholder="ZIP *" value={address.zip_code} onChange={e => setAddress(p => ({ ...p, zip_code: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500" data-testid="add-booking-zip" />
              </div>
            </div>
          </div>

          {/* Date & Schedule */}
          <div className="grid grid-cols-1 gap-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {/* Date Picker */}
                    {/* Zone Warning - if restricted */}
                    {serviceType === 'residential' && allowedDates && allowedDates.length > 0 && !override && (
                        <div className="mt-2 rounded-md bg-amber-50 p-2 text-xs text-amber-700">
                            <span className="font-semibold block mb-1">Zone {zone} Restriction:</span>
                            Only specific dates are available for this location.
                        </div>
                    )}
                    {/* Override Toggle */}
                    {serviceType === 'residential' && (
                        <div className="mt-3 flex items-center gap-2">
                            <input 
                                type="checkbox" 
                                id="overrideZone" 
                                checked={override} 
                                onChange={e => setOverride(e.target.checked)} 
                                className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <label htmlFor="overrideZone" className="text-xs font-medium text-slate-600 cursor-pointer flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3 text-amber-500" />
                                Override Zone Restrictions
                            </label>
                        </div>
                    )}
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <label className="block text-sm font-medium text-slate-700">Date *</label>
                        {zone && <span className="text-xs text-slate-500 ml-auto">{zone}</span>}
                    </div>
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" className={`w-full justify-start text-left text-sm font-normal ${!date ? 'text-slate-400' : 'text-slate-900'}`} data-testid="add-booking-date">
                                <CalendarDays className="mr-2 h-4 w-4 text-emerald-500" /> {date ? formatAdminDateDisplay(date) : 'Date'}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <CalendarWidget mode="single" selected={date ? new Date(date + 'T00:00:00') : undefined} onSelect={(d) => d && setDate(dateToStr(d))} disabled={getDisabledMatcher()} fromDate={ADMIN_BOOKING_MIN_DATE} toDate={ADMIN_BOOKING_MAX_DATE} defaultMonth={ADMIN_BOOKING_MIN_DATE} components={{ DayContent: (props) => renderDay(props.date) }} />
                        </PopoverContent>
                    </Popover>
                    <div className="mt-2 flex flex-wrap gap-2 text-[10px] text-slate-500" data-testid="add-booking-track-legend">
                      {(serviceType === 'commercial' || serviceType === 'residential') && (
                        <>
                          <span className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-blue-500"/> Crew A</span>
                          <span className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-orange-500"/> Crew B</span>
                        </>
                      )}
                      {serviceType === 'full_detailing' && (
                        <span className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-cyan-500"/> Detailing Crew</span>
                      )}
                    </div>
                </div>
                {/* Time Selection */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{serviceType === 'commercial' ? 'Start Time' : 'Time'}</label>
                    <Select key={timeSelectKey} value={time} onValueChange={setTime}>
                        <SelectTrigger data-testid="add-booking-time" disabled={!date || loadingSlots}><SelectValue placeholder={loadingSlots ? 'Loading times...' : 'Select time'} /></SelectTrigger>
                        <SelectContent>{getAdminTimeSlots(date, serviceType).map(t => {
                          const isUnavailable = date && (slotAvailability[t] === false || derivedUnavailableTimes[t]);
                          return <SelectItem key={t} value={t} disabled={isUnavailable} className={getTimeItemClass(isUnavailable)} data-testid={`admin-time-option-${t.replace(/[: ]/g, '-')}`}>{t}</SelectItem>;
                        })}</SelectContent>
                    </Select>
                    {loadingSlots && <p className="mt-1 text-[11px] text-slate-400" data-testid="admin-time-loading">Loading available times...</p>}
                </div>
                {serviceType === 'commercial' && (
                <>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">End Time</label><Select value={endTime} onValueChange={setEndTime}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{getAdminTimeSlots(date, serviceType).map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent></Select></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">Assign Crew</label><Select value={crewAssignment} onValueChange={setCrewAssignment}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="both">Both Crews</SelectItem><SelectItem value="crew_a">Crew A</SelectItem><SelectItem value="crew_b">Crew B</SelectItem></SelectContent></Select></div>
                </>
                )}
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Payment</label><Select value={payment} onValueChange={setPayment} disabled={processPayment && !bookingToEdit}><SelectTrigger data-testid="add-booking-payment"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="paid">Paid (General)</SelectItem><SelectItem value="paid-card">Paid (Credit Card)</SelectItem><SelectItem value="paid-cash">Paid (Cash)</SelectItem><SelectItem value="paid-zelle">Paid (Zelle)</SelectItem><SelectItem value="paid-check">Paid (Check)</SelectItem></SelectContent></Select></div>
                {/* Payment Processing Checkbox */}
                {(serviceType === 'residential' || serviceType === 'full_detailing') && !bookingToEdit && (
                  <div className="flex items-center gap-2 mt-2">
                    <input 
                      type="checkbox" 
                      id="processPayment" 
                      data-testid="add-booking-process-payment-checkbox"
                      checked={processPayment} 
                      onChange={e => {
                        setProcessPayment(e.target.checked);
                        if(e.target.checked) setPayment('pending'); // Stripe will update it
                      }}
                      className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <label htmlFor="processPayment" className="text-sm font-medium text-slate-700 cursor-pointer flex items-center gap-1">
                      Process Credit Card with Stripe
                      <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">(Opens checkout page)</span>
                    </label>
                  </div>
                )}
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Status</label><Select value={status} onValueChange={setStatus}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="confirmed">Confirmed</SelectItem></SelectContent></Select></div>
            </div>

            {date && (
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2" data-testid="booking-modal-schedule-title">
                    <Clock className="w-4 h-4 text-emerald-600" />
                    Schedule: {formatAdminDateDisplay(date)}
                  </h4>
                  {loadingSchedule && <span className="text-xs text-slate-500">Refreshing track…</span>}
                </div>

                <TrackSchedulePreview
                  date={formatAdminDateDisplay(date)}
                  serviceType={serviceType}
                  scheduleData={daySchedule}
                  slotAvailability={slotAvailability}
                  showAvailabilityHint={!loadingSlots}
                />
              </div>
            )}
          </div>

          <div><label className="block text-sm font-medium text-slate-700 mb-1">Notes</label><textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500 resize-none" placeholder="Internal notes..." data-testid="add-booking-notes" /></div>
        </div>

        <div className="flex justify-end gap-2 mt-6 pt-6 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={handleSubmit} disabled={isSubmitting} data-testid="create-booking-submit-btn"><CheckCircle className="w-4 h-4 mr-2" />{isSubmitting ? (bookingToEdit ? 'Updating...' : 'Creating...') : (bookingToEdit ? 'Update Booking' : 'Create Booking')}</Button>
        </div>
      </div>
    </div>
  );
}
